import { useMemo } from 'react'
import { TimeManagementListItem } from '@myapp/contract'
import { useTechnicianAppointments } from '../../../entities/appointment'
import { getDayRange } from '../../../shared/lib/blockRanges'
import { useTimelineDay } from './useTimelineDay'
import { useCollisionDetection } from '../../../shared/lib/collision'
import { useBreakBlocks } from '../../../entities/time-entry/model/useBreakBlock'
import { useTimelineTasksByTechnician } from './useTimelineTasks'
import { filterSchedulableTimeManagement } from '../../../shared/lib/timeline/weekHelpers'

export function useTimelineDayComposer() {
  const { dayIso } = useTimelineDay()
  const range = getDayRange(dayIso)

  const { data, isLoading, isFetching } = useTechnicianAppointments({ from: range.from, to: range.to })

  const breakBlocks = useBreakBlocks(data?.technicians, dayIso)
  const { getTasksForTechnician, timedTasksByTechnician } = useTimelineTasksByTechnician(range.from, range.to)

  const timeManagementByTechnician = useMemo(() => {
    const map = new Map<string, TimeManagementListItem[]>()
    if (!data?.technicians) return map
    for (const tech of data.technicians) {
      map.set(tech.technicianId, filterSchedulableTimeManagement(tech.timeManagementEntries ?? []))
    }
    return map
  }, [data?.technicians])

  const { hasCollision } = useCollisionDetection(
    data?.technicians, breakBlocks, timedTasksByTechnician, timeManagementByTechnician, dayIso,
  )

  const appointmentWorkOrderMap = useMemo(() => {
    const map = new Map<string, string>()
    if (!data?.technicians) return map
    for (const tech of data.technicians) {
      for (const appt of tech.appointments) {
        map.set(appt.id, appt.workOrderId)
      }
    }
    return map
  }, [data?.technicians])

  return {
    dayIso,
    range,
    data,
    isLoading,
    isFetching,
    breakBlocks,
    getTasksForTechnician,
    timedTasksByTechnician,
    hasCollision,
    appointmentWorkOrderMap,
  }
}
